/**
 * AppUpdateWatcher - notices when the server is serving a newer build.
 *
 * A single-page app keeps running whatever JavaScript it loaded, so a tab left
 * open across a deploy carries on with the old bundle indefinitely. This polls
 * the published manifest and compares it against the version compiled into this
 * bundle.
 *
 * Polls on a timer, and again whenever the tab regains focus or the network
 * comes back, since the common case is a laptop reopened the next morning.
 * Failures are ignored on purpose: a missing or unreachable manifest must never
 * interrupt someone, so the worst outcome is that the notice appears late.
 *
 * Renders nothing.
 */

import { useCallback, useEffect } from 'react';
import { useAppUpdateStore } from './appUpdateStore';
import type { Release } from '@/config/releases';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export function AppUpdateWatcher() {
  const applyManifest = useAppUpdateStore((s) => s.applyManifest);

  const check = useCallback(async () => {
    try {
      // Cache-busted twice over: a query string defeats intermediate caches,
      // no-store defeats the browser's. A cached manifest would report the
      // build the user is already running and the notice would never appear.
      const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return;

      const body = await response.json();
      const releases = body?.releases;
      if (Array.isArray(releases) && releases.length > 0) {
        applyManifest(releases as Release[]);
      }
    } catch {
      // Offline, or the manifest is not deployed yet. Try again next tick.
    }
  }, [applyManifest]);

  useEffect(() => {
    void check();

    const timer = window.setInterval(check, POLL_INTERVAL_MS);
    const onFocus = () => { if (document.visibilityState === 'visible') void check(); };

    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('online', check);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('online', check);
    };
  }, [check]);

  return null;
}
